/* ==========================================================================
   Aori K Beauty — brands-marquee.js
   Infinite 3-row marquee that responds to BOTH cursor and touch:
   - gentle base auto-drift (all devices)
   - cursor (mouse hover) position nudges speed/direction (fine pointers)
   - drag/swipe to scroll via Pointer Events (touch, pen, mouse-drag)
   - scroll-driven horizontal boost
   Reduced-motion: no auto-drift, but drag/swipe still works.

   Perf: the rAF loop does NOT start until window 'load' (so it never competes
   with FCP/LCP), fully stops (cancelAnimationFrame) when the section is off
   screen, and never reads layout in the scroll handler (visibility comes from
   IntersectionObserver, not getBoundingClientRect) — avoids forced reflow.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  function initMarquee(root) {
    var section = root.closest('.brands') || root;
    var tracks = [].slice.call(root.querySelectorAll('[data-track]'));
    if (!tracks.length) return;

    section.classList.add('js-marquee'); // take over from the CSS animation

    var BASE = reduce ? 0 : 0.45;   // auto drift (0 when reduced-motion)
    var MAX_CURSOR = 5, DEAD_ZONE = 0.12, LERP = 0.055;

    var cursorVel = 0, targetVel = 0, scrollBoost = 0, dragVel = 0;
    var vw = window.innerWidth;
    var mouseX = vw / 2;
    var active = true, dragging = false, started = false, rafId = null;

    var states = tracks.map(function (track) {
      return { el: track, speed: parseFloat(track.getAttribute('data-speed')) || 1, offset: 0, unit: 0 };
    });
    // Layout reads happen here only — once at start and on resize (never per-frame).
    function recalc() {
      vw = window.innerWidth;
      states.forEach(function (s) { s.unit = s.el.scrollWidth / 2; });
    }
    window.addEventListener('resize', recalc);

    function move(delta) { // delta px: positive scrolls content left (advance)
      states.forEach(function (s) {
        if (!s.unit) return;
        s.offset = ((s.offset + delta * s.speed) % s.unit + s.unit) % s.unit;
        s.el.style.transform = 'translateX(' + (-s.offset) + 'px)';
      });
    }

    /* Cursor hover (fine pointers only) */
    if (finePointer) {
      window.addEventListener('mousemove', function (e) { mouseX = e.clientX; }, { passive: true });
    }

    /* Scroll-driven boost — gated by the IO 'active' flag (no layout read here) */
    var lastScrollY = window.scrollY;
    window.addEventListener('scroll', function () {
      var dy = window.scrollY - lastScrollY; lastScrollY = window.scrollY;
      if (active) {
        scrollBoost += dy * 0.18;
        scrollBoost = Math.max(-14, Math.min(14, scrollBoost));
      }
    }, { passive: true });

    /* Drag / swipe — Pointer Events cover touch, pen and mouse-drag */
    var startX = 0, startY = 0, prevX = 0, decided = false, horizontal = false;
    root.addEventListener('pointerdown', function (e) {
      dragging = true; decided = false; horizontal = false;
      startX = prevX = e.clientX; startY = e.clientY; dragVel = 0;
    });
    root.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var totalX = e.clientX - startX, totalY = e.clientY - startY;
      if (!decided && (Math.abs(totalX) > 6 || Math.abs(totalY) > 6)) {
        decided = true;
        horizontal = Math.abs(totalX) > Math.abs(totalY);
        if (horizontal) { try { root.setPointerCapture(e.pointerId); } catch (err) {} }
      }
      if (decided && horizontal) {
        if (e.cancelable) e.preventDefault();
        var d = e.clientX - prevX; prevX = e.clientX;
        move(-d);          // drag right → content follows the finger
        dragVel = -d;      // carry momentum
      }
    });
    function endDrag() { dragging = false; }
    root.addEventListener('pointerup', endDrag);
    root.addEventListener('pointercancel', endDrag);
    root.addEventListener('pointerleave', endDrag);

    function frame() {
      if (finePointer && !reduce) {
        var norm = (mouseX / vw) * 2 - 1;
        var abs = Math.abs(norm), sign = norm >= 0 ? 1 : -1;
        targetVel = abs < DEAD_ZONE ? 0 : sign * ((abs - DEAD_ZONE) / (1 - DEAD_ZONE)) * MAX_CURSOR;
        cursorVel += (targetVel - cursorVel) * LERP;
        if (Math.abs(cursorVel) < 0.01) cursorVel = 0;
      }
      scrollBoost *= 0.92; if (Math.abs(scrollBoost) < 0.01) scrollBoost = 0;
      dragVel *= 0.9; if (Math.abs(dragVel) < 0.05) dragVel = 0;

      if (!dragging) {
        var v = BASE + cursorVel + scrollBoost + dragVel;
        if (v !== 0) move(v);
      }
      rafId = requestAnimationFrame(frame);
    }

    function start() { if (started && active && rafId === null) rafId = requestAnimationFrame(frame); }
    function stop() { if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; } }

    /* Pause (and free the main thread) when off-screen */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        active = entries[0].isIntersecting;
        if (active) start(); else stop();
      }, { threshold: 0 }).observe(section);
    }

    /* Defer the loop until the page has loaded so it never competes with FCP/LCP */
    function ready() { started = true; recalc(); start(); }
    if (document.readyState === 'complete') ready();
    else window.addEventListener('load', ready, { once: true });
  }

  function boot() { document.querySelectorAll('[data-marquee]').forEach(initMarquee); }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function (e) {
    var m = e.target.querySelector('[data-marquee]');
    if (m) initMarquee(m);
  });
})();
